/**
 * Editor для doc.online_store_order (Порція Роздріб.5b).
 *
 * FSM lifecycle: new → confirmed → preparing → shipped → delivered.
 * Окрема дія «Просунути статус» через POST /status.
 * Без проводок (заглушка фази 2) — backend просто змінює order_status.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature as nomApi, orgs } from '@/api/catalogs';
import {
  retailOnlineStoreOrders,
  type OnlineOrderStatus,
  type OnlineStoreOrderItemIn,
} from '@/api/retailOnlineStoreOrders';
import { retailSalesChannels } from '@/api/retailSalesChannels';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'retail.sales_channel.manage';

const STATUS_LABEL: Record<OnlineOrderStatus, string> = {
  new: 'Новий',
  confirmed: 'Підтверджений',
  preparing: 'Готується',
  shipped: 'Відправлений',
  delivered: 'Доставлений',
  cancelled: 'Скасований',
  returned: 'Повернений',
};

const NEXT_STATUSES: Record<OnlineOrderStatus, OnlineOrderStatus[]> = {
  new: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'cancelled'],
  preparing: ['shipped', 'cancelled'],
  shipped: ['delivered', 'returned'],
  delivered: ['returned'],
  cancelled: [],
  returned: [],
};

interface ItemRow extends OnlineStoreOrderItemIn {
  _key: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  sales_channel_id: string;
  external_order_id: string;
  external_order_status: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  delivery_address: string;
  delivery_amount: string;
  discount_amount: string;
  items: ItemRow[];
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  sales_channel_id: '',
  external_order_id: '',
  external_order_status: '',
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  delivery_address: '',
  delivery_amount: '0',
  discount_amount: '0',
  items: [],
  comment: '',
};

const newItem = (line: number): ItemRow => ({
  _key: crypto.randomUUID(),
  line_number: line,
  nomenclature_id: '',
  quantity: '1',
  price_per_unit: '0',
  discount_amount: '0',
});

export default function OnlineStoreOrderEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 1000 }),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const channelsQ = useQuery({
    queryKey: ['retail-sales-channels-for-order', form.organization_id],
    queryFn: () => retailSalesChannels.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-online-store-order', id],
    queryFn: () => retailOnlineStoreOrders.get(id!),
    enabled: !isNew,
  });

  const numberPreview = useNumberPreview('doc.online_store_order', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        sales_channel_id: d.sales_channel_id,
        external_order_id: d.external_order_id ?? '',
        external_order_status: d.external_order_status ?? '',
        customer_name: d.customer_name ?? '',
        customer_phone: d.customer_phone ?? '',
        customer_email: d.customer_email ?? '',
        delivery_address: d.delivery_address ?? '',
        delivery_amount: d.delivery_amount,
        discount_amount: d.discount_amount,
        items: d.items.map((it) => ({
          _key: it.id,
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity: it.quantity,
          price_per_unit: it.price_per_unit,
          discount_amount: it.discount_amount ?? '0',
        })),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const orderStatus = docQuery.data?.order_status as OnlineOrderStatus | undefined;
  const posted = !!docQuery.data?.posted;
  const isFinal = orderStatus === 'cancelled' || orderStatus === 'returned';

  const subtotal = useMemo(
    () =>
      form.items.reduce(
        (s, it) =>
          s +
          Number(it.quantity || 0) * Number(it.price_per_unit || 0) -
          Number(it.discount_amount || 0),
        0,
      ),
    [form.items],
  );
  const total = subtotal + Number(form.delivery_amount || 0) - Number(form.discount_amount || 0);

  const addItem = () => {
    const nextLine = form.items.length > 0 ? Math.max(...form.items.map((i) => i.line_number)) + 1 : 1;
    setForm({ ...form, items: [...form.items, newItem(nextLine)] });
  };

  const removeItem = (key: string) => {
    setForm({ ...form, items: form.items.filter((i) => i._key !== key) });
  };

  const updateItem = (key: string, patch: Partial<ItemRow>) => {
    setForm({
      ...form,
      items: form.items.map((i) => (i._key === key ? { ...i, ...patch } : i)),
    });
  };

  const payload = (): Parameters<typeof retailOnlineStoreOrders.create>[0] => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
    sales_channel_id: form.sales_channel_id,
    external_order_id: form.external_order_id || null,
    external_order_status: form.external_order_status || null,
    customer_name: form.customer_name || null,
    customer_phone: form.customer_phone || null,
    customer_email: form.customer_email || null,
    delivery_address: form.delivery_address || null,
    delivery_amount: form.delivery_amount,
    discount_amount: form.discount_amount,
    items: form.items.map(({ _key: _drop, ...rest }) => rest),
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () => retailOnlineStoreOrders.create(payload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-online-store-orders'] });
      navigate(`/retail/online-store-orders/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailOnlineStoreOrders.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        external_order_status: form.external_order_status || null,
        customer_name: form.customer_name || null,
        customer_phone: form.customer_phone || null,
        customer_email: form.customer_email || null,
        delivery_address: form.delivery_address || null,
        delivery_amount: form.delivery_amount,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-online-store-orders'] });
      qc.invalidateQueries({ queryKey: ['retail-online-store-order', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const changeStatus = useMutation({
    mutationFn: (newStatus: OnlineOrderStatus) =>
      retailOnlineStoreOrders.changeStatus(id!, newStatus),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['retail-online-store-order', id] }),
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailOnlineStoreOrders.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-online-store-orders'] });
      navigate('/retail/online-store-orders');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const nextStatuses = orderStatus ? NEXT_STATUSES[orderStatus] : [];

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        sales_channel_id: form.sales_channel_id,
      },
      {
        organization_id: 'Юрособа',
        sales_channel_id: 'Канал продажу',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Нове замовлення ${numberPreview ? `(${numberPreview})` : ''}`
            : `Замовлення ${form.document_number}`}
          {orderStatus && (
            <span style={{ marginLeft: '0.75rem', color: isFinal ? '#6b7280' : '#1d4ed8', fontSize: '1rem' }}>
              {STATUS_LABEL[orderStatus]}
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/retail/online-store-orders')}>Назад</button>
          {!isFinal && (
            <RequirePermission code={PERMISSION}>
              <button
                className="primary"
                onClick={handleSave}
                disabled={create.isPending || update.isPending || form.items.length === 0}
              >
                Зберегти
              </button>
            </RequirePermission>
          )}
          {!isNew && nextStatuses.length > 0 && (
            <RequirePermission code={PERMISSION}>
              <select
                onChange={(e) => {
                  const val = e.target.value as OnlineOrderStatus | '';
                  if (val) {
                    if (window.confirm(`Змінити статус на «${STATUS_LABEL[val]}»?`)) {
                      changeStatus.mutate(val);
                    }
                    e.target.value = '';
                  }
                }}
                defaultValue=""
              >
                <option value="">→ Просунути статус</option>
                {nextStatuses.map((s) => (
                  <option key={s} value={s}>
                    → {STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити замовлення?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <label>Дата замовлення</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isFinal}
          />
        </div>
        <div className="field">
          <RequiredLabel>Канал продажу</RequiredLabel>
          <select
            value={form.sales_channel_id}
            onChange={(e) => setForm({ ...form, sales_channel_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {channelsQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>ID у зовнішній системі</label>
          <input
            value={form.external_order_id}
            onChange={(e) => setForm({ ...form, external_order_id: e.target.value })}
            maxLength={100}
            disabled={!isNew}
          />
        </div>
        <div className="field">
          <label>Статус у зовнішній системі</label>
          <input
            value={form.external_order_status}
            onChange={(e) => setForm({ ...form, external_order_status: e.target.value })}
            maxLength={50}
            disabled={isFinal}
          />
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Покупець</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>ПIБ</label>
          <input
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            disabled={isFinal}
          />
        </div>
        <div className="field">
          <label>Телефон</label>
          <input
            value={form.customer_phone}
            onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
            disabled={isFinal}
          />
        </div>
        <div className="field">
          <label>Email</label>
          <input
            type="email"
            value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            disabled={isFinal}
          />
        </div>
        <div className="field">
          <label>Адреса доставки</label>
          <input
            value={form.delivery_address}
            onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
            disabled={isFinal}
          />
        </div>
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Позиції</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #cbd5e1', background: '#f1f5f9' }}>
            <th style={{ padding: '0.5rem', width: 50, textAlign: 'left' }}>#</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', width: 110, textAlign: 'right' }}>К-сть</th>
            <th style={{ padding: '0.5rem', width: 120, textAlign: 'right' }}>Ціна</th>
            <th style={{ padding: '0.5rem', width: 120, textAlign: 'right' }}>Знижка</th>
            <th style={{ padding: '0.5rem', width: 120, textAlign: 'right' }}>Сума</th>
            <th style={{ padding: '0.5rem', width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it) => {
            const rowSum =
              Number(it.quantity || 0) * Number(it.price_per_unit || 0) -
              Number(it.discount_amount || 0);
            return (
              <tr key={it._key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  <select
                    value={it.nomenclature_id}
                    onChange={(e) => updateItem(it._key, { nomenclature_id: e.target.value })}
                    disabled={isFinal}
                    style={{ width: '100%' }}
                  >
                    <option value="">—</option>
                    {nomQ.data?.items.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.code} — {n.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.001"
                    value={it.quantity}
                    onChange={(e) => updateItem(it._key, { quantity: e.target.value })}
                    disabled={isFinal}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={it.price_per_unit}
                    onChange={(e) => updateItem(it._key, { price_per_unit: e.target.value })}
                    disabled={isFinal}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={it.discount_amount ?? ''}
                    onChange={(e) => updateItem(it._key, { discount_amount: e.target.value })}
                    disabled={isFinal}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{rowSum.toFixed(2)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                  {!isFinal && (
                    <button onClick={() => removeItem(it._key)}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!isFinal && (
        <button onClick={addItem} style={{ marginTop: '0.5rem' }}>
          + Додати позицію
        </button>
      )}

      <div
        style={{
          marginTop: '1rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
        }}
      >
        <div className="field">
          <label>Сума доставки</label>
          <input
            type="number"
            step="0.01"
            value={form.delivery_amount}
            onChange={(e) => setForm({ ...form, delivery_amount: e.target.value })}
            disabled={isFinal}
          />
        </div>
        <div className="field">
          <label>Знижка на замовлення</label>
          <input
            type="number"
            step="0.01"
            value={form.discount_amount}
            onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
            disabled={isFinal}
          />
        </div>
        <div style={{ gridColumn: 'span 2', fontSize: '1.25rem' }}>
          Підсумок позицій: {subtotal.toFixed(2)} •{' '}
          <strong>Разом до сплати: {total.toFixed(2)} грн</strong>
        </div>
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <textarea
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          rows={2}
          disabled={isFinal}
        />
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
